import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { LifecycleStrip } from "../components/home/LifecycleStrip";
import { FAQS, FAQ_CATEGORIES } from "../lib/faqs";
import { Search, ChevronRight, Phone, MessageCircle } from "lucide-react";

export default function Help() {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return FAQS.filter((f) =>
      (category === "All" || f.c === category) &&
      (!q || f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q)));
  }, [query, category]);

  const schema = {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#fafcfa]">
      <Header />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
      <div className="bg-[#144733]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
          <div className="promo-stripe">
            <h1 className="heading-upper text-2xl sm:text-3xl text-white" data-testid="help-heading">{t("Help & support")}</h1>
            <p className="text-white/80 text-sm mt-2">{t("{{n}} answers, written by the people who run the counters.", { n: FAQS.length })}</p>
          </div>
          <div className="relative max-w-xl mt-6">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#57685e]" />
            <input className="field-input pl-9" placeholder={t("Search deposits, delivery, fines, documents…")} value={query}
              onChange={(e) => setQuery(e.target.value)} data-testid="help-search-input" />
          </div>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 w-full flex-1">
        <div className="flex flex-wrap gap-2 mb-6" data-testid="help-categories">
          {["All", ...FAQ_CATEGORIES].map((c) => (
            <button key={c} onClick={() => setCategory(c)}
              className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${category === c ? "bg-[#009900] text-white border-[#009900]" : "bg-white text-[#144733] border-[#d5ddd5] hover:border-[#009900]"}`}>
              {t(c)}
            </button>
          ))}
        </div>

        {shown.length === 0 && (
          <p className="text-sm text-[#57685e] py-8 text-center" data-testid="help-no-results">
            {t("Nothing matched \"{{query}}\" — try a different word, or chat with us in the corner.", { query })}
          </p>
        )}
        <div className="space-y-3" data-testid="help-faq-list">
          {shown.map((f) => (
            <details key={f.q} className="bg-white border border-[#E2E8F0] rounded-xl p-4 group">
              <summary className="cursor-pointer text-sm font-semibold text-[#0F1E17] list-none flex justify-between items-center gap-3">
                <span><span className="text-[10px] uppercase tracking-widest text-[#009900] block mb-0.5">{t(f.c)}</span>{t(f.q)}</span>
                <ChevronRight className="w-4 h-4 text-[#009900] group-open:rotate-90 transition-transform shrink-0" />
              </summary>
              <p className="text-sm text-[#57685e] mt-3 leading-relaxed">{t(f.a)}</p>
            </details>
          ))}
        </div>

        <div className="mt-10 bg-[#f0f9f0] border border-[#009900]/20 rounded-2xl p-6 flex flex-wrap gap-6 items-center justify-between" data-testid="help-contact-strip">
          <div className="flex items-center gap-3 text-sm text-[#144733]"><Phone className="w-5 h-5 text-[#009900]" /> <span><strong>800-EUROPCAR</strong>{t(" (800 38767227) — 24/7, humans included.")}</span></div>
          <div className="flex items-center gap-2 text-xs text-[#57685e]"><MessageCircle className="w-4 h-4 text-[#009900]" /> {t("Or chat with us in the bottom-right corner.")}</div>
        </div>
      </main>
      <LifecycleStrip />
      <Footer />
    </div>
  );
}
